import { defineMessages } from 'react-intl-next';
import RemoveIcon from '@atlaskit/icon/glyph/editor/remove';
import commonMessages from '@atlaskit/editor-common/messages';
import { clearHoverSelection, hoverTable } from './commands';
import { deleteTableWithAnalytics, toggleHeaderColumnWithAnalytics, toggleHeaderRowWithAnalytics, toggleNumberColumnWithAnalytics, insertRowWithAnalytics, deleteRowsWithAnalytics, mergeCellsWithAnalytics, splitCellWithAnalytics, deleteColumnsWithAnalytics, emptyMultipleCellsWithAnalytics, insertColumnWithAnalytics, wrapTableInExpandWithAnalytics } from './commands-with-analytics';
import { getPluginState } from './pm-plugins/plugin-factory';
import { pluginKey as tableResizingPluginKey } from './pm-plugins/table-resizing';
import { TableCssClassName } from './types';
import { isReferencedSource } from './utils/referentiality';
import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import { findCellRectClosestToPos, findTable, getSelectionRect, isSelectionType } from '@atlaskit/editor-tables/utils';
import { canMergeCells } from './transforms';
import { splitCell } from '@atlaskit/editor-tables/utils';
import tableMessages from './ui/messages';
import { messages as ContextualMenuMessages } from './ui/FloatingContextualMenu/ContextualMenu';
import { findParentDomRefOfType } from 'prosemirror-utils';
import { closestElement } from '@atlaskit/editor-common/utils';
import { getCopyButtonConfig, showCopyButton } from '../copy-button/toolbar';
// TODO: ED-14403 investigate why these translations don't work
export const messages = defineMessages({
  tableOptions: {
    id: 'fabric.editor.tableOptions',
    defaultMessage: 'Table options',
    description: 'Opens a menu with additional table options'
  },
  headerRow: {
    id: 'fabric.editor.headerRow',
    defaultMessage: 'Header row',
    description: 'Marks the first table row as a header row'
  },
  headerColumn: {
    id: 'fabric.editor.headerColumn',
    defaultMessage: 'Header column',
    description: 'Marks the first table column as a header row'
  },
  numberedColumn: {
    id: 'fabric.editor.numberedColumn',
    defaultMessage: 'Numbered column',
    description: 'Adds an auto-numbering column to your table'
  },
  collapseTable: {
    id: 'fabric.editor.collapseTable',
    defaultMessage: 'Collapse table',
    description: 'Wraps table in an expand'
  }
});
export const getToolbarMenuConfig = (config, state, {
  formatMessage
}, editorAnalyticsAPI) => {
  const options = [{
    id: 'editor.table.headerRow',
    title: formatMessage(messages.headerRow),
    onClick: toggleHeaderRowWithAnalytics(editorAnalyticsAPI),
    selected: state.isHeaderRowEnabled,
    hidden: !config.allowHeaderRow
  }, {
    id: 'editor.table.headerColumn',
    title: formatMessage(messages.headerColumn),
    onClick: toggleHeaderColumnWithAnalytics(editorAnalyticsAPI),
    selected: state.isHeaderColumnEnabled,
    hidden: !config.allowHeaderColumn
  }, {
    id: 'editor.table.numberedColumn',
    title: formatMessage(messages.numberedColumn),
    onClick: toggleNumberColumnWithAnalytics(editorAnalyticsAPI),
    selected: state.isNumberColumnEnabled,
    hidden: !config.allowNumberColumn
  }, {
    id: 'editor.table.collapseTable',
    title: formatMessage(messages.collapseTable),
    onClick: wrapTableInExpandWithAnalytics(editorAnalyticsAPI),
    selected: !!state.isTableCollapsed,
    disabled: !state.canCollapseTable,
    hidden: !config.allowCollapse
  }];
  return {
    id: 'editor.table.tableOptions',
    type: 'dropdown',
    title: formatMessage(messages.tableOptions),
    hidden: options.every(option => option.hidden),
    options
  };
}; // Added these options for mobile. Mobile bridge translates this menu and
// relay it to the native mobile. Native mobile displays the menu
// with native widgets. It's enabled via a plugin config.

export const getToolbarCellOptionsConfig = (editorState, initialSelectionRect, {
  formatMessage
}, getEditorContainerWidth, editorAnalyticsAPI) => {
  const {
    top,
    bottom,
    right,
    left
  } = initialSelectionRect;
  const numberOfColumns = right - left;
  const numberOfRows = bottom - top;
  const options = [{
    id: 'editor.table.insertColumn',
    title: formatMessage(tableMessages.insertColumn),
    onClick: (state, dispatch, view) => {
      const selectionRect = getClosestSelectionRect(state);
      const index = selectionRect === null || selectionRect === void 0 ? void 0 : selectionRect.right;

      if (index) {
        insertColumnWithAnalytics(getEditorContainerWidth, editorAnalyticsAPI)(INPUT_METHOD.FLOATING_TB, index)(state, dispatch, view);
      }

      return true;
    },
    selected: false,
    disabled: false
  }, {
    id: 'editor.table.insertRow',
    title: formatMessage(tableMessages.insertRow),
    onClick: (state, dispatch) => {
      const selectionRect = getClosestSelectionRect(state);
      const index = selectionRect === null || selectionRect === void 0 ? void 0 : selectionRect.bottom;

      if (index) {
        insertRowWithAnalytics(editorAnalyticsAPI)(INPUT_METHOD.FLOATING_TB, {
          index,
          moveCursorToInsertedRow: true
        })(state, dispatch);
      }

      return true;
    },
    selected: false,
    disabled: false
  }, {
    id: 'editor.table.removeColumns',
    title: formatMessage(tableMessages.removeColumns, {
      0: numberOfColumns
    }),
    onClick: (state, dispatch, view) => {
      const selectionRect = getClosestSelectionRect(state);

      if (selectionRect) {
        deleteColumnsWithAnalytics(editorAnalyticsAPI)(INPUT_METHOD.FLOATING_TB, selectionRect)(state, dispatch, view);
      }

      return true;
    },
    selected: false,
    disabled: false
  }, {
    id: 'editor.table.removeRows',
    title: formatMessage(tableMessages.removeRows, {
      0: numberOfRows
    }),
    onClick: (state, dispatch) => {
      const selectionRect = getClosestSelectionRect(state);

      if (selectionRect) {
        deleteRowsWithAnalytics(editorAnalyticsAPI)(INPUT_METHOD.FLOATING_TB, selectionRect, false)(state, dispatch);
      }

      return true;
    },
    selected: false,
    disabled: false
  }, {
    id: 'editor.table.mergeCells',
    title: formatMessage(ContextualMenuMessages.mergeCells),
    onClick: mergeCellsWithAnalytics(editorAnalyticsAPI),
    selected: false,
    disabled: !canMergeCells(editorState.tr)
  }, {
    id: 'editor.table.splitCell',
    title: formatMessage(ContextualMenuMessages.splitCell),
    onClick: splitCellWithAnalytics(editorAnalyticsAPI),
    selected: false,
    disabled: !splitCell(editorState)
  }, {
    id: 'editor.table.clearCells',
    title: formatMessage(ContextualMenuMessages.clearCells, {
      0: Math.max(numberOfColumns, numberOfRows)
    }),
    onClick: (state, dispatch) => {
      const {
        targetCellPosition
      } = getPluginState(state);
      emptyMultipleCellsWithAnalytics(editorAnalyticsAPI)(INPUT_METHOD.FLOATING_TB, targetCellPosition)(state, dispatch);
      return true;
    },
    selected: false,
    disabled: false
  }];
  return {
    id: 'editor.table.cellOptions',
    type: 'dropdown',
    title: formatMessage(tableMessages.cellOptions),
    hidden: true,
    options
  };
};

const getClosestSelectionRect = state => {
  const selection = state.selection;
  return isSelectionType(selection, 'cell') ? getSelectionRect(selection) : findCellRectClosestToPos(selection.$from);
};

export const getToolbarConfig = (getEditorContainerWidth, editorAnalyticsAPI) => config => (state, intl) => {
  const tableObject = findTable(state.selection);
  const pluginState = getPluginState(state);
  const resizeState = tableResizingPluginKey.getState(state);

  if (tableObject && pluginState.editorHasFocus) {
    const nodeType = state.schema.nodes.table;
    const menu = getToolbarMenuConfig(config, pluginState, intl, editorAnalyticsAPI);
    const cellItems = getCellItems(config, state, intl, getEditorContainerWidth, editorAnalyticsAPI); // Check if we need to show confirm dialog for delete button

    let confirmDialog;
    const localId = tableObject.node.attrs.localId;

    if (localId && isReferencedSource(state, localId)) {
      confirmDialog = {
        okButtonLabel: intl.formatMessage(tableMessages.confirmDeleteLinkedModalOKButton),
        message: intl.formatMessage(tableMessages.confirmDeleteLinkedModalMessage)
      };
    }

    const getDomRef = editorView => {
      let element;
      const domAtPos = editorView.domAtPos.bind(editorView);
      const parent = findParentDomRefOfType(nodeType, domAtPos)(state.selection);

      if (parent) {
        const tableRef = parent.querySelector('table') || undefined;

        if (tableRef) {
          element = closestElement(tableRef, `.${TableCssClassName.TABLE_NODE_WRAPPER}`) || undefined;
        }
      }

      return element;
    };

    return {
      title: 'Table floating controls',
      getDomRef,
      nodeType,
      offset: [0, 3],
      items: [menu, separator(menu.hidden), ...cellItems, {
        type: 'extensions-placeholder',
        separator: 'end'
      }, ...(state && showCopyButton(state) ? [getCopyButtonConfig(state, intl.formatMessage, nodeType, hoverTable(false, true), clearHoverSelection()), {
        type: 'separator'
      }] : []), {
        id: 'editor.table.delete',
        type: 'button',
        appearance: 'danger',
        icon: RemoveIcon,
        onClick: deleteTableWithAnalytics(editorAnalyticsAPI),
        disabled: !!resizeState && !!resizeState.dragging,
        onMouseEnter: hoverTable(true),
        onMouseLeave: clearHoverSelection(),
        title: intl.formatMessage(commonMessages.remove),
        confirmDialog
      }]
    };
  }

  return;
};

const separator = hidden => {
  return {
    type: 'separator',
    hidden: hidden
  };
};

const getCellItems = (pluginConfig, state, {
  formatMessage
}, getEditorContainerWidth, editorAnalyticsAPI) => {
  if (pluginConfig.allowCellOptionsInFloatingToolbar) {
    const initialSelectionRect = getClosestSelectionRect(state);

    if (initialSelectionRect) {
      const cellOptions = getToolbarCellOptionsConfig(state, initialSelectionRect, {
        formatMessage
      }, getEditorContainerWidth, editorAnalyticsAPI);
      return [cellOptions, separator(cellOptions.hidden)];
    }
  }

  return [];
};